import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Users, Loader2 } from 'lucide-react';
import axios from 'axios';
import config from '../../../config';

const API_URL = config.API_URL;

const CreateClientModal = ({ isOpen, onClose, token, onCreated }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Réinitialiser le state quand la modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setError(null);
    }
  }, [isOpen]);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Le nom du client est requis');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.post(
        `${API_URL}/api/client-management/clients`,
        // L'adresse ouvre l'accès client : vide, elle crée un client sans accès, ce qui est
        // le cas courant. La route s'attend aux deux champs, comme depuis l'écran Clients.
        { name: name.trim(), email: email.trim() || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onCreated();
      onClose();
    } catch (err) {
      console.error('[CREATE_CLIENT] Erreur:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreate();
    }
  };

  if (!isOpen) return null;

  const modalContentStyle = {
    maxWidth: '400px',
    width: '90vw',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    margin: 0
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e0d5'
  };

  const titleStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const closeButtonStyle = {
    padding: '0.5rem',
    borderRadius: '0.375rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex'
  };

  const bodyStyle = {
    padding: '1.5rem'
  };

  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    display: 'block',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid #e5e0d5',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const footerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e0d5',
    backgroundColor: '#f3efe7',
    borderRadius: '0 0 0.75rem 0.75rem',
    margin: 0
  };

  const buttonStyle = (primary) => ({
    padding: '0.625rem 1.25rem',
    borderRadius: '0.5rem',
    border: primary ? 'none' : '1px solid #e5e0d5',
    backgroundColor: primary ? '#1a1a1a' : 'white',
    color: primary ? 'white' : '#64748b',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: !name.trim() && primary ? 'not-allowed' : 'pointer',
    opacity: !name.trim() && primary ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  });

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    }}>
      <div style={{
        ...modalContentStyle,
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={titleStyle}>
            <Users size={20} />
            Nouveau client
          </div>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={bodyStyle}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>
              Nom du client <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              style={inputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nom de l'entreprise"
              autoFocus
            />
          </div>

          <div>
            <label style={labelStyle}>Email de connexion</label>
            <input
              type="email"
              style={inputStyle}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="client@exemple.com (optionnel)"
            />
          </div>

          {error && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', color: '#dc2626', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}
        </div>

        <div style={footerStyle}>
          <button style={buttonStyle(false)} onClick={onClose}>
            Annuler
          </button>
          <button
            style={buttonStyle(true)}
            onClick={handleCreate}
            disabled={!name.trim() || loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Création...
              </>
            ) : (
              'Créer'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateClientModal;
